import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useUser } from '../contexts/UserContext';
import { resolveType } from '@/services/ServiceContainer';
import { LeagueResultService } from '../services/LeagueResultService';
import { LeagueService } from '../services/LeagueService';
import { LeagueResultModel, LeagueMemberResultModel } from '../models/LeagueResultModels';
import { LeagueModel } from '../models/LeagueModels';
import { ButtonSpinner } from '../components/LoadingSpinner';
import Select from 'react-select';

const LeagueResults: React.FC = () => {
    const { leagueId_dayId, flightId } = useParams<{ leagueId_dayId: string; flightId?: string }>();
    const { theme } = useTheme();
    const { appUser } = useUser();
    const [results, setResults] = useState<LeagueResultModel | null>(null);
    const [league, setLeague] = useState<LeagueModel | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedFlightId, setSelectedFlightId] = useState<string>('');
    const [showPayoutModal, setShowPayoutModal] = useState(false);
    const [selectedMember, setSelectedMember] = useState<LeagueMemberResultModel | null>(null);
    const [showReRankedModal, setShowReRankedModal] = useState(false);
    const [clickedRankingType, setClickedRankingType] = useState<'gross' | 'net' | null>(null);
    const [isNavigating, setIsNavigating] = useState(false);

    useEffect(() => {
        const fetchResults = async () => {
            if (!leagueId_dayId) {
                setError('League ID and Day ID are required');
                setLoading(false);
                return;
            }

            const [leagueId, dayId] = leagueId_dayId.replace('_day', '__day').split('__');
            if (!leagueId || !dayId) {
                setError('Invalid League ID or Day ID format');
                setLoading(false);
                return;
            }

            try {
                const leagueResultsService = resolveType<LeagueResultService>(LeagueResultService);
                const leagueService = resolveType<LeagueService>(LeagueService);

                const [results, leagueData] = await Promise.all([
                    leagueResultsService.get(leagueId_dayId),
                    leagueService.get(leagueId)
                ]);

                setResults(results);
                setLeague(leagueData);

                // Set flight based on URL parameter or default to current user's flight
                if (flightId && leagueData?.flights?.some(f => f.id === flightId)) {
                    // Use flightId from URL if it's valid
                    setSelectedFlightId(flightId);
                } else if (appUser && leagueData?.members) {
                    const userMember = Object.values(leagueData.members).find(
                        member => member.encodedEmail === appUser.encodedEmail
                    );
                    if (userMember) {
                        setSelectedFlightId(userMember.flightId);
                    } else if (leagueData.flights && leagueData.flights.length > 0) {
                        setSelectedFlightId(leagueData.flights[0].id);
                    }
                } else if (leagueData?.flights && leagueData.flights.length > 0) {
                    setSelectedFlightId(leagueData.flights[0].id);
                }
            } catch (err) {
                setError('Failed to load league results');
                console.error('Error loading league results:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchResults();
    }, [leagueId_dayId, flightId, appUser]);

    const getDisplayName = (encodedEmail: string): string => {
        if (!league?.members) return 'Unknown Player';
        return league.members[encodedEmail]?.displayName || 'Unknown Player';
    };

    const formatCurrency = (amount: number | null): string => {
        if (amount === null || amount === undefined) return '$0.00';
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(amount);
    };

    const formatDate = (dayId: string): string => {
        if (!dayId) return '';
        const parts = dayId.replace('day_', '').split('_');
        if (parts.length !== 3) return dayId;

        const [year, month, day] = parts.map(Number);
        // Use Date.UTC to prevent timezone issues
        const date = new Date(Date.UTC(year, month - 1, day));

        return new Intl.DateTimeFormat('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            timeZone: 'UTC'
        }).format(date);
    };

    const getRankingMembers = (type: 'gross' | 'net') => {
        if (!results?.members || !selectedFlightId) return [];

        return Object.values(results.members)
            .filter(member => member.flightId === selectedFlightId)
            //            .filter(member => member.holesCompleted > 0 && member.flightId === selectedFlightId)
            .sort((a, b) => {
                if (type === 'gross') {
                    return a.grossScoreRanked - b.grossScoreRanked;
                } else {
                    return a.netScoreRanked - b.netScoreRanked;
                }
            });
    };

    const getSelectedFlightName = (): string => {
        if (!league?.flights || !selectedFlightId) return '';
        const flight = league.flights.find(f => f.id === selectedFlightId);
        return flight?.name || '';
    };

    const handlePayoutClick = (member: LeagueMemberResultModel) => {
        setSelectedMember(member);
        setShowPayoutModal(true);
    };

    const closePayoutModal = () => {
        setShowPayoutModal(false);
        setSelectedMember(null);
    };

    const handleReRankedClick = (rankingType: 'gross' | 'net') => {
        setClickedRankingType(rankingType);
        setShowReRankedModal(true);
    };

    const closeReRankedModal = () => {
        setShowReRankedModal(false);
        setClickedRankingType(null);
    };

    // Function to handle flight selection change
    const handleFlightChange = (option: any) => {
        const newFlightId = option?.value || '';
        setSelectedFlightId(newFlightId);

        // Update URL to include the selected flight
        if (results?.leagueId && results?.dayId) {
            const leagueId_dayId = `${results.leagueId}_${results.dayId}`;
            const newUrl = newFlightId ? `/league-results/${leagueId_dayId}/${newFlightId}` : `/league-results/${leagueId_dayId}`;
            window.history.replaceState(null, '', newUrl);
        }
    };

    const isCurrentUser = (member: LeagueMemberResultModel): boolean => {
        return appUser?.encodedEmail === member.encodedEmail;
    };

    // Function to find the next schedule item after the current day
    const getNextScheduleItem = () => {
        if (!league?.schedule || !results?.dayId) return null;

        const currentIndex = league.schedule.findIndex(s => s.dayId === results.dayId);
        if (currentIndex === -1 || currentIndex === league.schedule.length - 1) return null;

        return league.schedule[currentIndex + 1];
    };

    // Function to find the previous schedule item before the current day
    const getPreviousScheduleItem = () => {
        if (!league?.schedule || !results?.dayId) return null;

        const currentIndex = league.schedule.findIndex(s => s.dayId === results.dayId);
        if (currentIndex === -1 || currentIndex === 0) return null;

        return league.schedule[currentIndex - 1];
    };

    // Function to navigate to a specific league result day
    const navigateToLeagueResult = (dayId: string) => {
        setIsNavigating(true);
        const leagueId_dayId = `${results?.leagueId}_${dayId}`;
        const url = selectedFlightId ? `/league-results/${leagueId_dayId}/${selectedFlightId}` : `/league-results/${leagueId_dayId}`;
        window.location.href = url;
    };

    // Function to render the next week text or button
    const renderNextWeekContent = () => {
        const nextScheduleItem = getNextScheduleItem();

        if (!nextScheduleItem) {
            return 'See next week\'s results';
        }

        if (nextScheduleItem.status === 'completed') {
            return (
                <button
                    onClick={() => {
                        const nextDayId = nextScheduleItem.dayId;
                        const nextLeagueId_dayId = `${results?.leagueId}_${nextDayId}`;
                        const url = selectedFlightId ? `/league-results/${nextLeagueId_dayId}/${selectedFlightId}` : `/league-results/${nextLeagueId_dayId}`;
                        window.location.href = url;
                    }}
                    style={{
                        backgroundColor: theme.colors.PRIMARY,
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '8px 16px',
                        fontSize: '0.9rem',
                        fontWeight: '600',
                        cursor: 'pointer',
                        textDecoration: 'none'
                    }}
                >
                    View in Next Week's Results
                </button>
            );
        } else {
            return 'Waiting for next week\'s results...';
        }
    };

    if (loading) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '20px',
                backgroundColor: theme.colors.BACKGROUND
            }}>
                <ButtonSpinner size={24} color={theme.colors.PRIMARY} />
            </div>
        );
    }

    if (error) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                padding: '20px',
                color: theme.colors.ERROR,
                backgroundColor: theme.colors.BACKGROUND
            }}>
                {error}
            </div>
        );
    }

    if (!results || !league) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                padding: '20px',
                color: theme.colors.TEXT,
                backgroundColor: theme.colors.BACKGROUND
            }}>
                No results found for this league and day.
            </div>
        );
    }

    const grossRankings = getRankingMembers('gross');
    const netRankings = getRankingMembers('net');
    const wonPinPrizes = results.pinPrizes
        .filter(prize => prize.encodedEmail)
        .sort((a, b) => {
            // First sort by hole number
            if (a.hole !== b.hole) {
                return a.hole - b.hole;
            }
            // Then sort by tee name (handle null/undefined tee names)
            const aTeeName = a.teeName || '';
            const bTeeName = b.teeName || '';
            return aTeeName.localeCompare(bTeeName);
        });

    return (
        <div style={{
            padding: '8px',
            maxWidth: '1200px',
            width: '100%',
            margin: '0 auto',
            backgroundColor: theme.colors.BACKGROUND,
            boxSizing: 'border-box'
        }}>
            <h1 style={{
                color: theme.colors.TEXT,
                marginBottom: '8px',
                textAlign: 'center',
                fontSize: '1.75rem',
                fontWeight: '600'
            }}>
                Results for {formatDate(results.dayId)}
            </h1>
            {results?.dayId && (
                <p style={{
                    color: theme.colors.TEXT_SECONDARY,
                    textAlign: 'center',
                    fontSize: '1rem',
                    margin: 0,
                    marginBottom: '16px',
                }}>
                    {league.name}
                </p>
            )}

            {/* Flight Selector */}
            {league.flights.length > 0 && (
                <div style={{
                    backgroundColor: theme.colors.SURFACE,
                    borderRadius: '12px',
                    padding: '8px',
                    marginBottom: '16px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}>
                    <h2 style={{
                        color: theme.colors.TEXT,
                        marginBottom: '16px',
                        fontSize: '1.2rem',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: theme.spacing.MEDIUM
                    }}>
                        Flight:
                        <Select
                            value={selectedFlightId ? {
                                value: selectedFlightId,
                                label: getSelectedFlightName()
                            } : null}
                            onChange={handleFlightChange}
                            options={league.flights.map((flight) => ({
                                value: flight.id,
                                label: flight.name + (flight.id === league.members[appUser?.encodedEmail || '']?.flightId ? ' (My flight)' : '')
                            }))}
                            styles={{
                                control: (base: any) => ({
                                    ...base,
                                    backgroundColor: theme.colors.BACKGROUND,
                                    borderColor: theme.colors.BORDER,
                                    minWidth: '250px',
                                    '&:hover': {
                                        borderColor: theme.colors.BORDER
                                    }
                                }),
                                menu: (base: any) => ({
                                    ...base,
                                    backgroundColor: theme.colors.SURFACE,
                                    border: `1px solid ${theme.colors.BORDER}`,
                                    minWidth: '250px'
                                }),
                                option: (base: any, state: any) => ({
                                    ...base,
                                    backgroundColor: state.isFocused ? theme.colors.ACCENT : theme.colors.SURFACE,
                                    color: state.isFocused ? theme.colors.WHITE : theme.colors.TEXT,
                                    '&:hover': {
                                        backgroundColor: theme.colors.ACCENT,
                                        color: theme.colors.WHITE
                                    }
                                }),
                                singleValue: (base: any) => ({
                                    ...base,
                                    color: theme.colors.TEXT,
                                }),
                            }}
                        />
                    </h2>
                </div>
            )}

            {/* Gross Rankings Section */}
            <div style={{
                backgroundColor: theme.colors.SURFACE,
                borderRadius: '12px',
                padding: '8px 8px',
                marginBottom: '16px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',

            }}>
                <h2 style={{
                    color: theme.colors.TEXT,
                    marginBottom: '12px',
                    fontSize: '1.5rem',
                    fontWeight: '600'
                }}>
                    Gross Rankings
                </h2>
                {selectedFlightId && (
                    <div style={{
                        color: theme.colors.TEXT_SECONDARY,
                        fontSize: '1rem',
                        marginBottom: '20px'
                    }}>
                        {getSelectedFlightName()}
                    </div>
                )}
                <div style={{
                    borderRadius: '8px',
                    border: `1px solid ${theme.colors.BORDER}`,
                    overflowX: 'auto',
                }}>
                    <table style={{
                        borderCollapse: 'collapse',
                        width: '100%',
                        backgroundColor: theme.colors.BACKGROUND
                    }}>
                        <thead>
                            <tr style={{
                                backgroundColor: theme.colors.PRIMARY,
                                color: 'white'
                            }}>
                                <th style={{
                                    padding: '8px 8px',
                                    textAlign: 'center',
                                    fontWeight: '600',
                                    fontSize: '0.9rem',
                                }}>
                                    Player
                                </th>
                                <th style={{
                                    padding: '8px 8px',
                                    textAlign: 'center',
                                    fontWeight: '600',
                                    fontSize: '0.9rem',
                                }}>
                                    Gross
                                </th>
                                <th style={{
                                    padding: '8px 8px',
                                    textAlign: 'center',
                                    fontWeight: '600',
                                    fontSize: '0.9rem',
                                }}>
                                    Rank
                                </th>
                                <th style={{
                                    padding: '8px 8px',
                                    textAlign: 'center',
                                    fontWeight: '600',
                                    fontSize: '0.9rem',
                                }}>
                                    Payout
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {grossRankings.map((member, _) => (
                                <tr key={member.encodedEmail} style={{
                                    borderBottom: `1px solid ${theme.colors.BORDER}`,
                                    backgroundColor: isCurrentUser(member) ? theme.colors.HIGHLIGHTED_ITEM : 'transparent'
                                }}>
                                    <td style={{
                                        padding: '8px 8px',
                                        color: theme.colors.TEXT,
                                        fontWeight: '600',
                                        fontSize: '1rem',
                                    }}>
                                        {getDisplayName(member.encodedEmail)}
                                    </td>
                                    <td style={{
                                        padding: '8px 8px',
                                        color: theme.colors.TEXT_SECONDARY,
                                        fontSize: '0.9rem',
                                        textAlign: 'center',
                                    }}>
                                        {member.grossScore}
                                    </td>
                                    <td style={{
                                        padding: '8px 8px',
                                        textAlign: 'center',
                                    }}>
                                        {member.grossScoreRanked > 500 && member.holesCompleted > 0 ? (
                                            <button
                                                onClick={() => handleReRankedClick('gross')}
                                                title="Why is this rank different?"
                                                style={{
                                                    backgroundColor: theme.colors.SURFACE,
                                                    color: theme.colors.TEXT,
                                                    border: '1px solid ' + theme.colors.BORDER,
                                                    borderRadius: '6px',
                                                    padding: '8px 8px',
                                                    fontSize: '0.9rem',
                                                    fontWeight: '500',
                                                    cursor: 'pointer',
                                                }}
                                            >
                                                {member.grossRankText}
                                            </button>
                                        ) : (
                                            <span style={{
                                                color: theme.colors.TEXT_SECONDARY,
                                                fontSize: '0.9rem'
                                            }}>
                                                {member.grossRankText}
                                            </span>
                                        )}
                                    </td>
                                    <td style={{
                                        padding: '8px 8px',
                                        textAlign: 'center',
                                    }}>
                                        <button
                                            onClick={() => handlePayoutClick(member)}
                                            title="Click to view payout details"
                                            style={{
                                                backgroundColor: theme.colors.SURFACE,
                                                color: theme.colors.TEXT,
                                                border: '1px solid ' + theme.colors.BORDER,
                                                borderRadius: '6px',
                                                padding: '8px 8px',
                                                fontSize: '0.9rem',
                                                fontWeight: '500',
                                                cursor: 'pointer',
                                            }}
                                        >
                                            {formatCurrency((member.totalPayout || 0))}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Net Rankings Section */}
            <div style={{
                backgroundColor: theme.colors.SURFACE,
                borderRadius: '12px',
                padding: '8px 8px',
                marginBottom: '16px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}>
                <h2 style={{
                    color: theme.colors.TEXT,
                    marginBottom: '12px',
                    fontSize: '1.5rem',
                    fontWeight: '600'
                }}>
                    Net Rankings
                </h2>
                {selectedFlightId && (
                    <div style={{
                        color: theme.colors.TEXT_SECONDARY,
                        fontSize: '1rem',
                        marginBottom: '20px'
                    }}>
                        {getSelectedFlightName()}
                    </div>
                )}
                <div style={{
                    borderRadius: '8px',
                    border: `1px solid ${theme.colors.BORDER}`,
                    overflowX: 'auto'
                }}>
                    <table style={{
                        borderCollapse: 'collapse',
                        backgroundColor: theme.colors.BACKGROUND,
                        width: '100%'
                    }}>
                        <thead>
                            <tr style={{
                                backgroundColor: theme.colors.SECONDARY,
                                color: 'white'
                            }}>
                                <th style={{
                                    padding: '8px 8px',
                                    textAlign: 'center',
                                    fontWeight: '600',
                                    fontSize: '0.9rem',
                                }}>
                                    Player
                                </th>
                                <th style={{
                                    padding: '8px 8px',
                                    textAlign: 'center',
                                    fontWeight: '600',
                                    fontSize: '0.9rem',
                                }}>
                                    Hcp
                                </th>
                                <th style={{
                                    padding: '8px 8px',
                                    textAlign: 'center',
                                    fontWeight: '600',
                                    fontSize: '0.9rem',
                                }}>
                                    Net
                                </th>
                                <th style={{
                                    padding: '8px 8px',
                                    textAlign: 'center',
                                    fontWeight: '600',
                                    fontSize: '0.9rem',
                                }}>
                                    Rank
                                </th>
                                <th style={{
                                    padding: '8px 8px',
                                    textAlign: 'center',
                                    fontWeight: '600',
                                    fontSize: '0.9rem',
                                }}>
                                    Payout
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {netRankings.map((member, _) => (
                                <tr key={member.encodedEmail} style={{
                                    borderBottom: `1px solid ${theme.colors.BORDER}`,
                                    backgroundColor: isCurrentUser(member) ? theme.colors.HIGHLIGHTED_ITEM : 'transparent'
                                }}>
                                    <td style={{
                                        padding: '8px 8px',
                                        color: theme.colors.TEXT,
                                        fontWeight: '600',
                                        fontSize: '1rem',
                                    }}>
                                        {getDisplayName(member.encodedEmail)}
                                    </td>
                                    <td style={{
                                        padding: '8px 8px',
                                        color: theme.colors.TEXT_SECONDARY,
                                        fontSize: '0.9rem',
                                        textAlign: 'center',
                                    }}>
                                        {member.handicap}
                                    </td>
                                    <td style={{
                                        padding: '8px 8px',
                                        color: theme.colors.TEXT_SECONDARY,
                                        fontSize: '0.9rem',
                                        textAlign: 'center',
                                    }}>
                                        {member.netScore}
                                    </td>
                                    <td style={{
                                        padding: '8px 8px',
                                        textAlign: 'center',
                                    }}>
                                        {member.netScoreRanked > 500 && member.holesCompleted > 0 ? (
                                            <button
                                                onClick={() => handleReRankedClick('net')}
                                                title="Why is this rank different?"
                                                style={{
                                                    backgroundColor: theme.colors.SURFACE,
                                                    color: theme.colors.TEXT,
                                                    border: '1px solid ' + theme.colors.BORDER,
                                                    borderRadius: '6px',
                                                    padding: '8px 8px',
                                                    fontSize: '0.9rem',
                                                    fontWeight: '500',
                                                    cursor: 'pointer',
                                                }}
                                            >
                                                {member.netRankText}
                                            </button>
                                        ) : (
                                            <span style={{
                                                color: theme.colors.TEXT_SECONDARY,
                                                fontSize: '0.9rem'
                                            }}>
                                                {member.netRankText}
                                            </span>
                                        )}
                                    </td>
                                    <td style={{
                                        padding: '8px 8px',
                                        textAlign: 'center',
                                    }}>
                                        <button
                                            onClick={() => handlePayoutClick(member)}
                                            title="Click to view payout details"
                                            style={{
                                                backgroundColor: theme.colors.SURFACE,
                                                color: theme.colors.TEXT,
                                                border: '1px solid ' + theme.colors.BORDER,
                                                borderRadius: '6px',
                                                padding: '8px 8px',
                                                fontSize: '0.9rem',
                                                fontWeight: '500',
                                                cursor: 'pointer',
                                            }}
                                        >
                                            {formatCurrency((member.totalPayout || 0))}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Pin Prizes Section */}
            <div style={{
                backgroundColor: theme.colors.SURFACE,
                borderRadius: '12px',
                padding: '8px 8px',
                marginBottom: '16px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}>
                <h2 style={{
                    color: theme.colors.TEXT,
                    marginBottom: '20px',
                    fontSize: '1.5rem',
                    fontWeight: '600'
                }}>
                    Pin Prizes (All Flights)
                </h2>
                {wonPinPrizes.length === 0 ? (
                    <div style={{
                        color: theme.colors.TEXT_SECONDARY,
                        textAlign: 'center',
                        padding: '40px 20px'
                    }}>
                        No pin prizes were won today.
                    </div>
                ) : (
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                        gap: '20px'
                    }}>
                        {wonPinPrizes.map((prize) => (
                            <div key={prize.id} style={{
                                backgroundColor: theme.colors.BACKGROUND,
                                borderRadius: '8px',
                                padding: '20px',
                                border: '1px solid',
                                borderColor: theme.colors.BORDER,
                                textAlign: 'center'
                            }}>
                                <div style={{
                                    color: theme.colors.PRIMARY,
                                    fontWeight: '600',
                                    fontSize: '1.2rem',
                                    marginBottom: '12px'
                                }}>
                                    {getDisplayName(prize.encodedEmail!)}
                                </div>
                                <div style={{
                                    color: theme.colors.TEXT,
                                    fontWeight: '500',
                                    marginBottom: '8px'
                                }}>
                                    {prize.description}
                                </div>
                                <div style={{
                                    color: theme.colors.TEXT_SECONDARY,
                                    fontSize: '0.9rem',
                                    marginBottom: '12px'
                                }}>
                                    Hole {prize.hole}
                                    {prize.teeName && ` • ${prize.teeName} Tees`}
                                </div>
                                <div style={{
                                    color: theme.colors.SUCCESS,
                                    fontWeight: '600',
                                    fontSize: '1.1rem'
                                }}>
                                    {formatCurrency(prize.amount)}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Gross Skins Section */}
            <div style={{
                backgroundColor: theme.colors.SURFACE,
                borderRadius: '12px',
                padding: '8px 8px',
                marginBottom: '16px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}>
                <h2 style={{
                    color: theme.colors.TEXT,
                    marginBottom: '20px',
                    fontSize: '1.5rem',
                    fontWeight: '600'
                }}>
                    Gross Skins (All Flights)
                </h2>
                {(results.grossSkinPrizes || []).length > 0 ? (
                    <div style={{
                        borderRadius: '8px',
                        border: `1px solid ${theme.colors.BORDER}`,
                        overflowX: 'auto'
                    }}>
                        <table style={{
                            borderCollapse: 'collapse',
                            width: '100%',
                            backgroundColor: theme.colors.BACKGROUND
                        }}>
                            <thead>
                                <tr style={{
                                    backgroundColor: theme.colors.PRIMARY,
                                    color: 'white'
                                }}>
                                    <th style={{
                                        padding: '8px 8px',
                                        textAlign: 'center',
                                        fontWeight: '600',
                                        fontSize: '0.9rem',
                                    }}>
                                        Hole
                                    </th>
                                    <th style={{
                                        padding: '8px 8px',
                                        textAlign: 'left',
                                        fontWeight: '600',
                                        fontSize: '0.9rem',
                                    }}>
                                        Player
                                    </th>
                                    <th style={{
                                        padding: '8px 8px',
                                        textAlign: 'center',
                                        fontWeight: '600',
                                        fontSize: '0.9rem',
                                    }}>
                                        Gross
                                    </th>
                                    <th style={{
                                        padding: '8px 8px',
                                        textAlign: 'center',
                                        fontWeight: '600',
                                        fontSize: '0.9rem',
                                    }}>
                                        Amount
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {(results.grossSkinPrizes || []).map((skin, index) => (
                                    <tr key={index} style={{
                                        borderBottom: `1px solid ${theme.colors.BORDER}`,
                                        backgroundColor: isCurrentUser({ encodedEmail: skin.encodedEmail || '' } as LeagueMemberResultModel) ? theme.colors.HIGHLIGHTED_ITEM : 'transparent'
                                    }}>
                                        <td style={{
                                            padding: '8px 8px',
                                            textAlign: 'center',
                                            color: theme.colors.TEXT_SECONDARY,
                                            fontSize: '0.9rem',
                                        }}>
                                            {skin.hole}
                                        </td>
                                        <td style={{
                                            padding: '8px 8px',
                                            color: theme.colors.TEXT,
                                            fontWeight: '600',
                                            fontSize: '1rem',
                                        }}>
                                            {getDisplayName(skin.encodedEmail || '')}
                                        </td>
                                        <td style={{
                                            padding: '8px 8px',
                                            textAlign: 'center',
                                            color: theme.colors.TEXT_SECONDARY,
                                            fontSize: '0.9rem',
                                        }}>
                                            {skin.grossScore}
                                        </td>
                                        <td style={{
                                            padding: '8px 8px',
                                            textAlign: 'center',
                                            color: theme.colors.TEXT_SECONDARY,
                                            fontSize: '0.9rem',
                                        }}>
                                            {formatCurrency(skin.amount)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '16px',
                            borderTop: `2px solid ${theme.colors.PRIMARY}`,
                            backgroundColor: theme.colors.BACKGROUND
                        }}>
                            <div style={{
                                fontWeight: '700',
                                color: theme.colors.PRIMARY,
                                fontSize: '1.1rem'
                            }}>
                                {(results.grossSkinPrizes || []).length} Winners
                            </div>
                            <div style={{
                                fontWeight: '700',
                                color: theme.colors.PRIMARY,
                                fontSize: '1.1rem'
                            }}>
                                {formatCurrency((results.grossSkinPrizes || []).reduce((total, skin) => total + (skin.amount || 0), 0))}
                            </div>
                        </div>
                    </div>
                ) : (
                    <div style={{
                        color: theme.colors.TEXT_SECONDARY,
                        textAlign: 'center',
                        padding: '40px 20px'
                    }}>
                        {league?.schedule?.find(s => s.dayId === results.dayId)?.skinsDayId1 ? 'No gross skins were won today.' : renderNextWeekContent()}
                    </div>
                )}
            </div>

            {/* Net Skins Section */}
            <div style={{
                backgroundColor: theme.colors.SURFACE,
                borderRadius: '12px',
                padding: '8px 8px',
                marginBottom: '76px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}>
                <h2 style={{
                    color: theme.colors.TEXT,
                    marginBottom: '20px',
                    fontSize: '1.5rem',
                    fontWeight: '600'
                }}>
                    Net Skins (All Flights)
                </h2>
                {(results.netSkinPrizes || []).length > 0 ? (
                    <div style={{
                        borderRadius: '8px',
                        border: `1px solid ${theme.colors.BORDER}`,
                        overflowX: 'auto'
                    }}>
                        <table style={{
                            borderCollapse: 'collapse',
                            width: '100%',
                            backgroundColor: theme.colors.BACKGROUND
                        }}>
                            <thead>
                                <tr style={{
                                    backgroundColor: theme.colors.SECONDARY,
                                    color: 'white'
                                }}>
                                    <th style={{
                                        padding: '8px 8px',
                                        textAlign: 'center',
                                        fontWeight: '600',
                                        fontSize: '0.9rem',
                                    }}>
                                        Hole
                                    </th>
                                    <th style={{
                                        padding: '8px 8px',
                                        textAlign: 'left',
                                        fontWeight: '600',
                                        fontSize: '0.9rem',
                                    }}>
                                        Player
                                    </th>
                                    <th style={{
                                        padding: '8px 8px',
                                        textAlign: 'center',
                                        fontWeight: '600',
                                        fontSize: '0.9rem',
                                    }}>
                                        Grs
                                    </th>
                                    <th style={{
                                        padding: '8px 8px',
                                        textAlign: 'center',
                                        fontWeight: '600',
                                        fontSize: '0.9rem',
                                    }}>
                                        Net
                                    </th>
                                    <th style={{
                                        padding: '8px 8px',
                                        textAlign: 'center',
                                        fontWeight: '600',
                                        fontSize: '0.9rem',
                                    }}>
                                        Amount
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {(results.netSkinPrizes || []).map((skin, index) => (
                                    <tr key={index} style={{
                                        borderBottom: `1px solid ${theme.colors.BORDER}`,
                                        backgroundColor: isCurrentUser({ encodedEmail: skin.encodedEmail || '' } as LeagueMemberResultModel) ? theme.colors.HIGHLIGHTED_ITEM : 'transparent'
                                    }}>
                                        <td style={{
                                            padding: '8px 8px',
                                            textAlign: 'center',
                                            color: theme.colors.TEXT_SECONDARY,
                                            fontSize: '0.9rem',
                                        }}>
                                            {skin.hole}
                                        </td>
                                        <td style={{
                                            padding: '8px 8px',
                                            color: theme.colors.TEXT,
                                            fontWeight: '600',
                                            fontSize: '1rem',
                                        }}>
                                            {getDisplayName(skin.encodedEmail || '')}
                                        </td>
                                        <td style={{
                                            padding: '8px 8px',
                                            textAlign: 'center',
                                            color: theme.colors.TEXT_SECONDARY,
                                            fontSize: '0.9rem',
                                        }}>
                                            {skin.grossScore}
                                        </td>
                                        <td style={{
                                            padding: '8px 8px',
                                            textAlign: 'center',
                                            color: theme.colors.TEXT_SECONDARY,
                                            fontSize: '0.9rem',
                                        }}>
                                            {skin.netScore}
                                        </td>
                                        <td style={{
                                            padding: '8px 8px',
                                            textAlign: 'center',
                                            color: theme.colors.TEXT_SECONDARY,
                                            fontSize: '0.9rem',
                                        }}>
                                            {formatCurrency(skin.amount)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '16px',
                            borderTop: `2px solid ${theme.colors.PRIMARY}`,
                            backgroundColor: theme.colors.BACKGROUND
                        }}>
                            <div style={{
                                fontWeight: '700',
                                color: theme.colors.PRIMARY,
                                fontSize: '1.1rem'
                            }}>
                                {(results.netSkinPrizes || []).length} Winners
                            </div>
                            <div style={{
                                fontWeight: '700',
                                color: theme.colors.PRIMARY,
                                fontSize: '1.1rem'
                            }}>
                                {formatCurrency((results.netSkinPrizes || []).reduce((total, skin) => total + (skin.amount || 0), 0))}
                            </div>
                        </div>
                    </div>
                ) : (
                    <div style={{
                        color: theme.colors.TEXT_SECONDARY,
                        textAlign: 'center',
                        padding: '40px 20px'
                    }}>
                        {league?.schedule?.find(s => s.dayId === results.dayId)?.skinsDayId1 ? 'No net skins were won today.' : renderNextWeekContent()}
                    </div>
                )}
            </div>

            {/* Payout Details Modal */}
            {
                showPayoutModal && selectedMember && (
                    <div style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        height: '100vh',
                        width: '100%',
                        backgroundColor: 'rgba(0,0,0,0.4)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1000,
                        padding: '20px',
                        boxSizing: 'border-box'
                    }}
                        onClick={closePayoutModal}
                    >
                        <div
                            style={{
                                backgroundColor: theme.colors.BACKGROUND,
                                borderRadius: '12px',
                                padding: '32px',
                                maxHeight: '90vh',
                                overflowY: 'auto',
                                boxShadow: '0 4px 24px rgba(0,0,0,0.2)',
                                position: 'relative'
                            }}
                            onClick={e => e.stopPropagation()}
                        >
                            <h2 style={{
                                marginTop: 0,
                                marginBottom: '20px',
                                color: theme.colors.TEXT,
                                fontWeight: 700,
                                fontSize: '1.3rem',
                                textAlign: 'center'
                            }}>
                                Payout Details
                            </h2>
                            <div style={{
                                marginBottom: '20px',
                                color: theme.colors.TEXT,
                                fontWeight: 600,
                                textAlign: 'center'
                            }}>
                                {getDisplayName(selectedMember.encodedEmail)}
                            </div>
                            {selectedMember.payouts && selectedMember.payouts.length > 0 ? (
                                <>
                                    <div style={{
                                        marginBottom: '20px',
                                        overflowX: 'auto'
                                    }}>
                                        <table style={{
                                            borderCollapse: 'collapse'
                                        }}>
                                            <thead>
                                                <tr style={{ backgroundColor: theme.colors.SURFACE }}>
                                                    <th style={{
                                                        padding: '12px 8px',
                                                        textAlign: 'left',
                                                        fontWeight: 600,
                                                        fontSize: '0.95rem',
                                                    }}>
                                                        Description
                                                    </th>
                                                    <th style={{
                                                        padding: '12px 8px',
                                                        textAlign: 'center',
                                                        fontWeight: 600,
                                                        fontSize: '0.95rem',
                                                    }}>
                                                        Amount
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {selectedMember.payouts.map((payout, idx) => (
                                                    <tr key={payout.id || idx}>
                                                        <td style={{
                                                            padding: '12px 8px',
                                                            color: theme.colors.TEXT
                                                        }}>
                                                            {payout.description || payout.code || 'Payout'} {payout.hole ? ' - Hole ' + payout.hole : ''}
                                                        </td>
                                                        <td style={{
                                                            padding: '12px 8px',
                                                            color: theme.colors.TEXT_SECONDARY,
                                                            textAlign: 'center',
                                                        }}>
                                                            {formatCurrency(payout.amount)}
                                                        </td>
                                                    </tr>
                                                ))}
                                                {/* {(results.grossSkinPrizes || []).filter(p => p.encodedEmail === selectedMember.encodedEmail).map((payout, idx) => (
                                                    <tr key={payout.id || idx}>
                                                        <td style={{
                                                            padding: '12px 8px',
                                                            color: theme.colors.TEXT
                                                        }}>
                                                            {(payout.grossScore || 0) > 0 ? 'Gross Skin - Hole ' + payout.hole : ''}
                                                        </td>
                                                        <td style={{
                                                            padding: '12px 8px',
                                                            color: theme.colors.TEXT_SECONDARY,
                                                            textAlign: 'center',
                                                        }}>
                                                            {formatCurrency(payout.amount)}
                                                        </td>
                                                    </tr>
                                                ))}
                                                {(results.netSkinPrizes || []).filter(p => p.encodedEmail === selectedMember.encodedEmail).map((payout, idx) => (
                                                    <tr key={payout.id || idx}>
                                                        <td style={{
                                                            padding: '12px 8px',
                                                            color: theme.colors.TEXT
                                                        }}>
                                                            {(payout.netScore || 0) > 0 ? 'Net Skin - Hole ' + payout.hole : ''}
                                                        </td>
                                                        <td style={{
                                                            padding: '12px 8px',
                                                            color: theme.colors.TEXT_SECONDARY,
                                                            textAlign: 'center',
                                                        }}>
                                                            {formatCurrency(payout.amount)}
                                                        </td>
                                                    </tr>
                                                ))} */}
                                                <tr>
                                                    <td style={{
                                                        padding: '12px 8px',
                                                        color: theme.colors.TEXT,
                                                        fontWeight: 700
                                                    }}>
                                                        Total
                                                    </td>
                                                    <td style={{
                                                        padding: '12px 8px',
                                                        color: theme.colors.TEXT_SECONDARY,
                                                        textAlign: 'center',
                                                        fontWeight: 700,
                                                    }}>
                                                        {formatCurrency((selectedMember.payouts.reduce((sum, p) => sum + (p.amount || 0), 0)))}
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </>
                            ) : (
                                <div style={{
                                    color: theme.colors.TEXT_SECONDARY,
                                    textAlign: 'center',
                                    marginBottom: '20px'
                                }}>
                                    No payouts for this player.
                                </div>
                            )}
                            <button
                                onClick={closePayoutModal}
                                style={{
                                    display: 'block',
                                    margin: '0 auto',
                                    padding: '12px 24px',
                                    backgroundColor: theme.colors.PRIMARY,
                                    color: theme.colors.WHITE,
                                    border: 'none',
                                    borderRadius: '6px',
                                    fontWeight: 600,
                                    fontSize: '1rem',
                                    cursor: 'pointer'
                                }}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                )
            }

            {/* Re-ranked Details Modal */}
            {
                showReRankedModal && (
                    <div style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        height: '100vh',
                        width: '100%',
                        backgroundColor: 'rgba(0,0,0,0.4)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1000,
                        padding: '20px',
                        boxSizing: 'border-box'
                    }}
                        onClick={closeReRankedModal}
                    >
                        <div
                            style={{
                                backgroundColor: theme.colors.BACKGROUND,
                                borderRadius: '12px',
                                padding: '32px',
                                maxHeight: '90vh',
                                overflowY: 'auto',
                                boxShadow: '0 4px 24px rgba(0,0,0,0.2)',
                                position: 'relative'
                            }}
                            onClick={e => e.stopPropagation()}
                        >
                            <h2 style={{
                                marginTop: 0,
                                marginBottom: '20px',
                                color: theme.colors.TEXT,
                                fontWeight: 700,
                                fontSize: '1.3rem',
                                textAlign: 'center'
                            }}>
                                Why is this rank different?
                            </h2>
                            <div style={{
                                color: theme.colors.TEXT_SECONDARY,
                                textAlign: 'center',
                                marginBottom: '20px',
                                lineHeight: '1.5'
                            }}>
                                <p style={{ marginBottom: '16px' }}>
                                    This player won more prize money for their {clickedRankingType === 'gross' ? 'Net Ranking' : 'Gross Ranking'}.
                                </p>
                                <p>
                                    A player can only win one of Gross Rank or Net Rank prizes, so this score was removed from the {clickedRankingType === 'gross' ? 'Gross ' : 'Net '} Ranking.
                                </p>
                            </div>
                            <button
                                onClick={closeReRankedModal}
                                style={{
                                    display: 'block',
                                    margin: '0 auto',
                                    padding: '12px 24px',
                                    backgroundColor: theme.colors.PRIMARY,
                                    color: theme.colors.WHITE,
                                    border: 'none',
                                    borderRadius: '6px',
                                    fontWeight: 600,
                                    fontSize: '1rem',
                                    cursor: 'pointer'
                                }}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                )
            }

            {/* Navigation FAB Buttons */}
            <div style={{
                position: 'fixed',
                bottom: '20px',
                left: '20px',
                zIndex: 100
            }}>
                {getPreviousScheduleItem() && (
                    <button
                        onClick={() => navigateToLeagueResult(getPreviousScheduleItem()!.dayId)}
                        disabled={isNavigating}
                        style={{
                            width: '48px',
                            height: '48px',
                            borderRadius: '50%',
                            backgroundColor: theme.colors.BUTTON_SECONDARY_BG,
                            color: theme.colors.BUTTON_SECONDARY_TEXT,
                            border: 'none',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                            cursor: isNavigating ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '20px',
                            fontWeight: 'bold',
                            opacity: isNavigating ? 0.7 : 1
                        }}
                        title="Previous Week"
                    >
                        {isNavigating ? (
                            <ButtonSpinner size={20} color="white" />
                        ) : (
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M12 20L4 12L12 4L13.41 5.41L7.83 11H20V13H7.83L13.41 18.59L12 20Z" fill="currentColor" />
                            </svg>
                        )}
                    </button>
                )}
            </div>

            <div style={{
                position: 'fixed',
                bottom: '20px',
                right: '20px',
                zIndex: 100
            }}>
                {getNextScheduleItem() && getNextScheduleItem()!.status === 'completed' && (
                    <button
                        onClick={() => navigateToLeagueResult(getNextScheduleItem()!.dayId)}
                        disabled={isNavigating}
                        style={{
                            width: '48px',
                            height: '48px',
                            borderRadius: '50%',
                            backgroundColor: theme.colors.BUTTON_SECONDARY_BG,
                            color: theme.colors.BUTTON_SECONDARY_TEXT,
                            border: 'none',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                            cursor: isNavigating ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '20px',
                            fontWeight: 'bold',
                            opacity: isNavigating ? 0.7 : 1
                        }}
                        title="Next Week"
                    >
                        {isNavigating ? (
                            <ButtonSpinner size={20} color="white" />
                        ) : (
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M12 4L20 12L12 20L10.59 18.59L16.17 13H4V11H16.17L10.59 5.41L12 4Z" fill="currentColor" />
                            </svg>
                        )}
                    </button>
                )}
            </div>
        </div>
    );
};

export default LeagueResults; 